import { z } from 'zod';
/**
 * **Retention, and the right to be forgotten** — spec §7.6, as a vocabulary.
 *
 * §7.6 was decided a long time before anything called it: the book is the
 * tenant's record and is never auto-purged; a guest's deletion is
 * *anonymization, not row deletion*; operational data has numbers; backups age
 * out rather than being edited. `docs/status-2026-09-21.md` §5 put the
 * consequence in one line — "anonymisation never runs" — and this file, with
 * `anonymizeClient` in `packages/store`, is the caller it never had.
 *
 * What lives here is the part every surface must agree on letter for letter:
 * what a guest may ask for, what state an ask is in, how long the owner has,
 * and the retention numbers the privacy page states. The wiping itself is the
 * store's; the pages are the theme's; the *words* a lawyer will pass are
 * nobody's here (guest-site-plan D4).
 *
 * ## The one rule everything here exists to keep
 *
 * **A request is a task for the owner, never a verb for the guest.** A guest
 * on `/visits` files an ask; the ask lands as an owner task with a clock on it;
 * the owner (or the operator's CLI) runs it. Nothing runs from a browser
 * session, and that is not a trust question about the guest — it is that a
 * cookie is not a person. A stolen session that could wipe the book of the
 * guest it names would be a way to hurt somebody with their own phone, and an
 * export that a session could pull would be a way to read a stranger's history
 * from a café's wifi. Both go through a human who can pick up the telephone.
 */
/* ------------------------------------------------------------------ */
/* What a guest may ask for                                            */
/* ------------------------------------------------------------------ */
/**
 * `export` — the rows the platform holds about this person, as one JSON
 * document the owner hands over (§7.5's "the owner can always leave with their
 * book", applied to one guest). `delete` — §7.6's anonymization: identities,
 * name, notes and preferences wiped; ledger rows keep every amount.
 */
export const DATA_REQUEST_KINDS = ['export', 'delete'];
export const DataRequestKind = z.enum(DATA_REQUEST_KINDS);
/**
 * `open` — filed and waiting for the owner. `done` — the owner ran it; for a
 * deletion the client row is anonymized, for an export the document was
 * produced. `cancelled` — the owner closed it without running it (the guest
 * telephoned and changed her mind; the ask was a duplicate she meant for
 * another business). A CHECK pins the set; there is no fourth state, because
 * "running" is a transaction and either commits or never happened.
 */
export const DATA_REQUEST_STATUSES = ['open', 'done', 'cancelled'];
export const DataRequestStatus = z.enum(DATA_REQUEST_STATUSES);
/**
 * Who filed it. `guest` is the session on `/visits`; `staff` is the desk filing
 * one for a guest who telephoned; `operator` is `yourstore retention request`
 * — which is also the partner's door: agreement §6.6 says the spa owner "can
 * request deletion of a guest's data through us", and *through us* means a
 * person at the desk or at the console, never the partner's wire. There is
 * deliberately no `partner` value and no grant behind one.
 */
export const DATA_REQUEST_ORIGINS = ['guest', 'staff', 'operator'];
export const DataRequestOrigin = z.enum(DATA_REQUEST_ORIGINS);
/**
 * **Thirty days** from the ask to the owner's answer — the clock the task shows.
 *
 * The number is the one the common privacy regimes converge on for an access
 * or erasure request (a month, extendable when the request is complex), and
 * it is stated here as a fact about *this platform's* promise rather than
 * about any law: the privacy page says "within thirty days", the task says how
 * many are left, and the CLI's list marks the ones that are over. Counsel may
 * move it (D4); it moves in one place.
 */
export const DATA_REQUEST_DEADLINE_DAYS = 30;
/** The instant an open request is due, from the instant it was filed. */
export function dataRequestDueAt(createdAt) {
    return new Date(createdAt.getTime() + DATA_REQUEST_DEADLINE_DAYS * 24 * 60 * 60 * 1000);
}
/* ------------------------------------------------------------------ */
/* The numbers §7.6 decided                                            */
/* ------------------------------------------------------------------ */
/**
 * Spec §7.6's numbers, once, so the privacy page and any future sweep read the
 * same fact. Every value is what the spec says today; changing one here is a
 * spec change and should be argued there first.
 *
 *  - `events_months` — the outbox is kept 24 months, then archived to Storage.
 *  - `webhook_deliveries_days` / `idempotency_keys_days` / `logs_days` — the
 *    three operational tables with a number.
 *  - `backup_pitr_days` / `backup_export_days` — how long an anonymization
 *    takes to reach every backup, because backups age out rather than being
 *    edited: "7 days for PITR, 90 for exports — and the terms say so".
 *  - `offboarding_delete_days` — after a tenant leaves with their export
 *    (§7.5), the data is deleted 90 days later.
 */
export const RETENTION = {
    events_months: 24,
    webhook_deliveries_days: 90,
    idempotency_keys_days: 30,
    logs_days: 30,
    backup_pitr_days: 7,
    backup_export_days: 90,
    offboarding_delete_days: 90,
};
/* ------------------------------------------------------------------ */
/* One request, as every surface reads it                              */
/* ------------------------------------------------------------------ */
/**
 * The wire shape of one request — what `/api/data-requests` answers the guest,
 * what `/v1/data-requests` answers the desk (with `guest_name` beside it), and
 * what the CLI prints. `due_at` is derived from `created_at` by
 * `dataRequestDueAt` on the server, and carried rather than recomputed so a
 * deadline the platform once promised does not move when the constant does.
 */
export const DataRequestSummary = z.object({
    data_request_id: z.string(),
    kind: DataRequestKind,
    status: DataRequestStatus,
    requested_by: DataRequestOrigin,
    created_at: z.string(),
    due_at: z.string(),
    resolved_at: z.string().nullable(),
});
//# sourceMappingURL=retention.js.map